import { classnames } from '@/helpers/general';
import { ReactNode } from 'react';

export type BookingFlowColumnGap = 'default' | 'narrow';

type Props = { children: ReactNode };
type ColumnsProps = Props & { columnGap?: BookingFlowColumnGap };
type MainColumnProps = Props & { sticky?: boolean };

const BookingFlowColumns = ({ children, columnGap = 'default' }: ColumnsProps) => (
  <div
    className={classnames(
      'gap-cv-margin flex flex-wrap justify-between lg:flex-nowrap',
      columnGap === 'narrow' && 'lg:gap-xl',
    )}>
    {children}
  </div>
);

export const MainColumn = ({ children, sticky }: MainColumnProps) => (
  <div
    className={classnames(
      'min-w-0 flex-1',
      // `self-start` with a height of its own is what gives the pin room to travel: the
      // containing block is the columns row, which a taller side column stretches. The
      // offsets clear the fixed CheckoutHeader and leave the same gap at the bottom edge,
      // and the ceiling is what makes the content scroll inside the column instead of
      // scrolling the page.
      //
      // Below `lg` it pins too, clearing the fixed header (64px, 72px from `md`; no extra bleed
      // margin below `lg`) and reserving whatever height the bottomBar actually renders at,
      // published as `--bottom-bar-h` by BookingFlowPage — the bar's content (and safe-area
      // padding) isn't a fixed size, so a literal pixel offset here would drift out of sync
      // with it. The bar is gone from `lg`, where the bottom gap is the bleed margin instead.
      sticky &&
        classnames(
          'sticky top-[64px] flex h-[calc(100svh-64px-var(--bottom-bar-h,0px))] flex-col self-start',
          'md:top-[72px] md:h-[calc(100svh-72px-var(--bottom-bar-h,0px))]',
          'lg:top-[96px] lg:h-[calc(100svh-136px)]',
        ),
    )}>
    {children}
  </div>
);

export const SideColumn = ({ children }: Props) => (
  <div className="hidden w-full lg:block lg:w-[320px] lg:shrink-0">{children}</div>
);

export default BookingFlowColumns;
